import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import {
  adjustColumnTotal,
  adjustCurrencyTotals,
  insertByIdDesc,
  isDropTarget,
} from '@/lib/kanban-utils';
import type {
  KanbanBoardName,
  KanbanBoardResponse,
  KanbanColumn,
  KanbanColumnKey,
  KanbanQueryParams,
} from '@/lib/types';

/**
 * One kanban board — load, filter, and move a card between columns.
 *
 * The board endpoint is cheap to call and expensive to call often: it is rate
 * limited **per IP**, shared by everyone behind one office NAT, so three boards
 * wired to an undebounced search box all spend from one budget. Two rules
 * follow from that, and they are why this is a hook rather than an effect per
 * board:
 *
 * ⚠️ *SUPERSEDED: "the rate limit is 120 requests per 60 seconds … three boards
 * wired to an undebounced search box burn through it in about fifteen
 * seconds."* It is **12,000/60s** (corrected 2026-09-17), so that fifteen
 * seconds is off by a factor of a hundred. Both rules below stand on their own
 * shape — a request per keystroke and a request per drag gesture are the wrong
 * shape whatever the ceiling is — but neither may be argued from that
 * arithmetic any more.
 *
 *  - every query change is debounced by {@link DEBOUNCE_MS} and aborts the
 *    request it supersedes;
 *  - **a successful move never refetches the board.** `PATCH .../{id}/stage`
 *    returns the moved card and nothing else — no refreshed counts, no
 *    refreshed totals — so the column headers are this hook's bookkeeping to
 *    get right. Re-reading the board after each drag would be one request per
 *    gesture, for a response that carries nothing the move did not already
 *    give us.
 */

/** Long enough to swallow a burst of keystrokes, short enough not to feel laggy. */
const DEBOUNCE_MS = 250;

/**
 * Serialise `params` into a stable string.
 *
 * **`params` reaches this hook as an object literal built in the caller's
 * render, so it is a new reference on every single render.** Putting it in a
 * dependency array directly is an infinite fetch loop — the effect reruns,
 * setState rerenders, a fresh literal appears, the effect reruns. A string
 * compares by value, so the effect below depends on THIS and never on `params`.
 *
 * The keys are walked in a fixed order, so `{search, ownerId}` and
 * `{ownerId, search}` produce the same key, and blank values are dropped the
 * way `listQuery` drops them — which makes the result not merely a cache key
 * but the exact param object to send, recoverable with `JSON.parse`.
 */
function paramsKeyOf(params: KanbanQueryParams): string {
  const stable: Record<string, string | number> = {};
  /*
   * Every key is walked, SORTED — deliberately not an allow-list.
   *
   * There was one, of five keys, and when the API gained per-board filters it
   * would have silently dropped every new one before the request was even
   * built: exactly the silent-ignore failure the server just fixed, mirrored on
   * this side. An allow-list here has to be kept in sync with a type by hand,
   * and nothing fails when it isn't. Sorting gives the same stable key without
   * anything to forget.
   */
  for (const key of Object.keys(params).sort()) {
    const value = (params as Record<string, unknown>)[key];
    // Narrowed rather than cast: every board param is a string or a number, and
    // anything else would serialise into the request as garbage.
    if (typeof value !== 'string' && typeof value !== 'number') continue;
    if (value === '') continue;
    stable[key] = value;
  }
  return JSON.stringify(stable);
}

/**
 * Human copy for a failed move.
 *
 * The useful messages land in `error.details` under the board's own column key
 * — "No stage with id 99", 'Stage "Old Pipeline" is inactive and cannot be
 * moved into' — so that is the first place to look. Two cases never appear
 * there, and both are ordinary:
 *
 *  - an unrecognised-key 422 reports at an **empty path**, so it is in
 *    `details` but filed under `''` rather than under any field name;
 *  - a **403** carries no `details` at all, and its message is the API's role
 *    list ("Requires one of: basic_user, advanced_user, admin, super_admin"),
 *    which is a fact about the API rather than anything to show a user.
 *
 * `error.message` is the fallback for everything else, and it is already humane
 * for the cases that matter — the 429 copy is rewritten in `api.ts`.
 */
function moveErrorMessage(caught: unknown, columnKey: KanbanColumnKey): string {
  if (!(caught instanceof ApiError)) {
    return caught instanceof Error
      ? caught.message
      : 'Could not move that card. It has been put back.';
  }
  if (caught.status === 403) return 'Your account cannot move cards.';
  if (caught.status === 404) {
    return 'That record no longer exists — someone deleted it. Reloading the board.';
  }
  return caught.fieldErrors[columnKey] ?? caught.formMessage ?? caught.message;
}

/*
 * ⚠️ Do not reintroduce a confirmation step on a card move (a
 * `KanbanMoveConfirmation`, a `moveConfirmation` state, or an
 * `acknowledgement` argument on the move function). Moving a quote into its
 * accepting stage does not raise an invoice or push to Xero, and no code from
 * the move endpoint asks to be told to go ahead — every board's move is a
 * plain success-or-failure. A move that fails is a `moveError` and is rolled
 * back; there is no third outcome.
 */

export interface UseKanbanResult {
  board: KanbanBoardResponse['data'] | null;
  meta: KanbanBoardResponse['meta'] | null;
  /** First load only. */
  loading: boolean;
  /** A reload while data is already on screen — for aria-busy + opacity-60. */
  refetching: boolean;
  error: string | null;
  /** A failed move, already rolled back. Separate from `error` so the board stays usable. */
  moveError: string | null;
  dismissMoveError: () => void;
  /** The card currently being moved, for a pending affordance. */
  movingCardId: number | null;
  moveCard: (cardId: number, fromColumnId: number | null, toColumnId: number) => Promise<void>;
  reload: () => void;
}

export function useKanban(
  boardName: KanbanBoardName,
  params: KanbanQueryParams,
  enabled: boolean,
): UseKanbanResult {
  const [board, setBoard] = useState<KanbanBoardResponse['data'] | null>(null);
  const [meta, setMeta] = useState<KanbanBoardResponse['meta'] | null>(null);
  const [loadingState, setLoadingState] = useState(enabled);
  const [refetchingState, setRefetchingState] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [movingCardId, setMovingCardId] = useState<number | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  /*
   * `moveCard` reads and rewrites the board without depending on it, so it
   * keeps one identity across every drag instead of invalidating every
   * memoised card the moment one moves. The ref is the truth about what is on
   * screen right now; `setBoard` only tells React about it.
   */
  const boardRef = useRef<KanbanBoardResponse['data'] | null>(null);
  const metaRef = useRef<KanbanBoardResponse['meta'] | null>(null);
  /** Set by `reload()` so a deliberate refresh does not sit through the debounce. */
  const skipDebounceRef = useRef(false);

  const paramsKey = paramsKeyOf(params);

  /*
   * A disabled board is never busy. Derived during render rather than cleared
   * from the effect, so toggling to the table view mid-load cannot leave a
   * board stuck behind a skeleton or dimmed at 60% — and so nothing calls
   * setState synchronously in an effect to fix it up afterwards.
   */
  const loading = enabled && loadingState;
  const refetching = enabled && refetchingState;

  const reload = useCallback(() => {
    skipDebounceRef.current = true;
    setReloadNonce((nonce) => nonce + 1);
  }, []);

  const dismissMoveError = useCallback(() => setMoveError(null), []);

  useEffect(() => {
    /*
     * `enabled: false` fetches NOTHING. The boards sit behind a view toggle and
     * the table view must not pay for three board requests it will not render.
     * Nothing needs clearing on the way out — `loading` and `refetching` are
     * gated on `enabled` during render.
     */
    if (!enabled) return;

    const controller = new AbortController();
    const { signal } = controller;
    const delay = skipDebounceRef.current ? 0 : DEBOUNCE_MS;
    skipDebounceRef.current = false;

    const timer = setTimeout(() => {
      // The first load blocks with skeletons; every later one dims what is
      // already there, because a board that empties itself on each keystroke is
      // harder to read than a slightly stale one.
      if (boardRef.current) setRefetchingState(true);
      else setLoadingState(true);

      // The key IS the params, canonicalised — see `paramsKeyOf`.
      api
        .kanban(boardName, JSON.parse(paramsKey) as KanbanQueryParams, signal)
        .then((response) => {
          if (signal.aborted) return;
          boardRef.current = response.data;
          metaRef.current = response.meta;
          setBoard(response.data);
          setMeta(response.meta);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal.aborted) return;
          // The stale board stays on screen underneath: a failed refetch is not
          // a reason to throw away data the user was reading.
          setError(
            caught instanceof Error ? caught.message : `Failed to load the ${boardName} board.`,
          );
        })
        .finally(() => {
          if (signal.aborted) return;
          setLoadingState(false);
          setRefetchingState(false);
        });
    }, delay);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [boardName, paramsKey, enabled, reloadNonce]);

  /** One move — a drag, or the Move menu, which is the same action. */
  const moveCard = useCallback(
    async (cardId: number, fromColumnId: number | null, toColumnId: number) => {
      /*
       * Normalise before comparing. Drag libraries hand ids back as STRINGS off
       * DOM data attributes, and `'4' === 4` is false — so an unnormalised
       * same-column guard silently never fires, the reducer below then runs
       * with one column as both source and target, and its count and total get
       * moved twice in opposite directions.
       *
       * A source id that will not parse is read as the "Unassigned" column,
       * whose real id is `null` — the only column an id can legitimately be
       * missing from.
       */
      const id = Number(cardId);
      const parsedFrom = fromColumnId === null ? null : Number(fromColumnId);
      const from = parsedFrom === null || Number.isNaN(parsedFrom) ? null : parsedFrom;
      const to = Number(toColumnId);
      if (!Number.isFinite(id) || !Number.isFinite(to)) return;

      /*
       * Same-column drop: RETURN WITHOUT A REQUEST. There is no per-card
       * ordering in this contract, so `PATCH { stageId: <the stage it is
       * already in> }` cannot express anything — the server answers 200 having
       * changed nothing, and the optimistic bookkeeping would meanwhile pull a
       * card out of a column and put it back while decrementing and
       * incrementing the very same header.
       */
      if (from === to) return;

      /** The EXACT pre-move board, held by reference so rollback is identical. */
      const snapshot = boardRef.current;
      const currentMeta = metaRef.current;
      if (!snapshot || !currentMeta) return;

      const source = snapshot.columns.find((column) => column.id === from);
      const target = snapshot.columns.find((column) => column.id === to);
      const moved = source?.cards.find((card) => card.id === id);

      if (!target) {
        setMoveError('That column is no longer on the board.');
        reload();
        return;
      }
      // "Unassigned" (id null) and retired columns are drag-OUT only. The server
      // agrees — an inactive column is a 422 — but refusing here keeps a
      // pointless request off the limiter and lets the copy be specific.
      if (!isDropTarget(target)) {
        setMoveError(`"${target.name}" cannot accept cards.`);
        return;
      }
      if (!source || !moved) {
        setMoveError('That card is no longer on the board.');
        reload();
        return;
      }

      /*
       * The move response is ONE CARD and carries no refreshed counts or
       * totals, so both headers are adjusted here or they stay wrong until the
       * next load. `hasMore` is `count > cards.length` by definition —
       * recomputed rather than carried, so the "+n more" note cannot drift.
       *
       * The COUNT is adjusted on every board; the TOTAL only on the boards that
       * have one. A column whose `totalAmount` is null belongs to a board with
       * no money in it at all, and `adjustColumnTotal` hands the null straight
       * back — the header keeps saying nothing about money rather than
       * inventing a figure out of a drag. That is the money bookkeeping alone:
       * the card still leaves one column and lands in the other, both counts
       * still move, and `hasMore` is still recomputed from the new count.
       */
      const optimisticColumns: KanbanColumn[] = [];
      for (const column of snapshot.columns) {
        if (column === source) {
          const cards = column.cards.filter((card) => card.id !== id);
          const count = column.count - 1;
          /*
           * A retired column is in the payload only while something is still in
           * it — the server drops it once its filtered count reaches zero. An
           * empty ghost left here would be contradicted by the very next load.
           * "Unassigned" is exempt: it is synthetic, its id is null, and
           * nothing confirms the server drops it when it empties.
           */
          if (count <= 0 && !column.isActive && column.id !== null) continue;
          optimisticColumns.push({
            ...column,
            cards,
            count,
            totalAmount: adjustColumnTotal(column.totalAmount, moved.amount, -1),
            totalsByCurrency: adjustCurrencyTotals(column.totalsByCurrency, moved, -1, {
              cards,
              hasMore: count > cards.length,
            }),
            hasMore: count > cards.length,
          });
        } else if (column === target) {
          const cards = insertByIdDesc(column.cards, moved);
          const count = column.count + 1;
          optimisticColumns.push({
            ...column,
            cards,
            count,
            totalAmount: adjustColumnTotal(column.totalAmount, moved.amount, 1),
            totalsByCurrency: adjustCurrencyTotals(column.totalsByCurrency, moved, 1, {
              cards,
              hasMore: count > cards.length,
            }),
            hasMore: count > cards.length,
          });
        } else {
          optimisticColumns.push(column);
        }
      }

      const optimistic = { ...snapshot, columns: optimisticColumns };
      boardRef.current = optimistic;
      setBoard(optimistic);
      setMoveError(null);
      setMovingCardId(id);

      try {
        // `moveEndpoint` and `columnKey` both come off the board response —
        // nothing about this request is per-board knowledge held here.
        const response = await api.moveKanbanCard(
          currentMeta.moveEndpoint,
          snapshot.columnKey,
          id,
          to,
        );
        /*
         * If a reload landed while the PATCH was in flight, its answer is newer
         * than anything reconstructed from the snapshot — leave it alone. The
         * identity check is the whole test: nothing else writes `boardRef`.
         */
        if (boardRef.current !== optimistic) return;
        // Swap the optimistic card for the server's: its `updatedAt` has moved,
        // and it may carry an edit somebody else made in the meantime.
        const settled = {
          ...optimistic,
          columns: optimistic.columns.map((column) =>
            column.id === to
              ? {
                  ...column,
                  cards: column.cards.map((card) => (card.id === id ? response.data : card)),
                }
              : column,
          ),
        };
        boardRef.current = settled;
        setBoard(settled);
      } catch (caught: unknown) {
        /*
         * ROLL BACK FIRST. The move did not take, so the board must stop saying
         * it did before anything else happens — a card left sitting in the
         * target column asserts the opposite of what the server just said.
         */
        if (boardRef.current === optimistic) {
          boardRef.current = snapshot;
          setBoard(snapshot);
        }

        setMoveError(moveErrorMessage(caught, snapshot.columnKey));
        // A 404 means the record went away underneath the board, so the rest of
        // it is suspect too. This is the one failure worth spending a request on.
        if (caught instanceof ApiError && caught.status === 404) reload();
      } finally {
        setMovingCardId(null);
      }
    },
    [reload],
  );

  return {
    board,
    meta,
    loading,
    refetching,
    error,
    moveError,
    dismissMoveError,
    movingCardId,
    moveCard,
    reload,
  };
}
